
import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { CheckCircle, CreditCard, Clock, FileCheck } from "lucide-react";
import { cookieExistsAndValid } from '@/utils/cookies';
import { useState, useEffect } from "react";
import { useLoader } from "@/contexts/LoaderContext";
import { API_BASE_URL } from '@/config';
import axios from "@/lib/axios";
import JourneyHeader from "@/components/JourneyHeader";

const Home = () => {
  const { enc_id } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [visibleLogin, setVisibleLogin] = useState(false);
  const [bapLogo, setBapLogo] = useState<string | null>(null);
  const [bapId, setBapId] = useState<string | null>(null);
  const { loading, setLoading } = useLoader();

  const checkBapIdentifier = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/check_bap_identifier_active_session/`, {
        withCredentials: true, // send HttpOnly cookie
      });
      setVisibleLogin(res.data.valid); // show logo if session valid
      setBapLogo(res.data.bap_logo || null);
    } catch (err) {
      setVisibleLogin(false);
      setBapLogo(null);
      if (err.response) {
        console.log("Invalid cookie, status:", err.response.status);
        console.log(err.response.data.detail);
      } else {
        console.error("Network error or no response");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // First, try to load BAP info from sessionStorage (set by HomeGateway)
    const sessionBapId = sessionStorage.getItem('whitespace_bap_id');
    const sessionBapLogo = sessionStorage.getItem('whitespace_bap_logo');

    if (sessionBapId) {
      setBapId(sessionBapId);
      console.log('[HomeWhitespace] BAP ID from sessionStorage:', sessionBapId);
    } else {
      setBapId(null);
    }

    if (sessionBapLogo) {
      setBapLogo(sessionBapLogo);
      console.log('[HomeWhitespace] BAP Logo from sessionStorage:', sessionBapLogo);
    } else {
      setBapLogo(null);
      console.log('[HomeWhitespace] No BAP Logo found in sessionStorage');
    }

    if (enc_id) {
      console.log('[HomeWhitespace] enc_id detected:', enc_id);
      if (enc_id === 'WHITESPACE') {
        const mode = import.meta.env.MODE;
        const allowedModes = ['development', 'register'];
        if (!allowedModes.includes(mode)) {
          navigate('/404', { replace: true });
          return;
        }

        // WHITESPACE journey: Show BAP logo in header and make login buttons visible
        setVisibleLogin(true);
        console.log('[HomeWhitespace] WHITESPACE journey - showing login buttons');
        console.log('[HomeWhitespace] BAP Logo state:', { bapId: sessionBapId, bapLogo: sessionBapLogo });
        return;
      }

      // Regular journey (enc_id is not WHITESPACE): Show login button
      // If enc_id is present, we should set the session and get the logo
      const setSession = async () => {
        setLoading(true);
        try {
          const searchParams = new URLSearchParams(location.search);
          const params = Object.fromEntries(searchParams.entries());
          console.log('[HomeWhitespace] Setting BAP identifier:', { enc_id, params });
          const res = await axios.post(`${API_BASE_URL}/api/set_bap_identifier_cookie/`, {
            enc_id,
            ...params
          });
          setVisibleLogin(true);
          // Use API BAP logo if available, fallback to sessionStorage
          const apiBapLogo = res.data.bap_logo || sessionBapLogo;
          if (apiBapLogo) {
            setBapLogo(apiBapLogo);
          }
        } catch (err) {
          console.error("Error setting BAP identifier:", err);
          setVisibleLogin(false);
        } finally {
          setLoading(false);
        }
      };

      setSession();
      return;
    }

    checkBapIdentifier();
  }, [enc_id, location.search]);

  const handleGetStarted = () => {
    if (enc_id) {
      navigate(`/consent2/${enc_id}${location.search}`, {
        state: { from: 'home' }
      });

      return;
    }
    navigate('/whitespace/index');
  };

  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Header */}
      <JourneyHeader />

      {/* Hero section - more compact for mobile */}
      <section className="bg-intellend-green text-intellend-white py-8 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-5xl font-bold mb-4">
              Financial Solutions for Indian MSMEs
            </h1>
            <p className="text-sm md:text-xl mb-6 opacity-90">
              Access business loans with transparent terms and competitive rates
            </p>
            {visibleLogin &&
              <Button
                className="bg-intellend-gold hover:bg-intellend-gold/90 text-intellend-white px-6 py-2 md:px-8 md:py-6 md:h-auto"
                onClick={handleGetStarted}
              >
                Get Started
              </Button>
            }
          </div>
        </div>
      </section>

      {/* Features section - compact grid for mobile */}
      <section className="py-8 md:py-16 bg-intellend-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-center text-intellend-green mb-6 md:mb-12">
            Why Choose Intellend?
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <CreditCard className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Loan amounts upto 1Cr</h3>
              {!isMobile && (
                <p className="text-gray-600">Loan amounts upto 1 crore for all your business needs</p>
              )}
            </div>

            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <CheckCircle className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Fast Processing</h3>
              {!isMobile && (
                <p className="text-gray-600">Quick decisions get funds in your account when you need them.</p>
              )}
            </div>

            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <FileCheck className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Interest Rates starting at 1% per month</h3>
              {!isMobile && (
                <p className="text-gray-600">Low interest rates starting from 1% per month.</p>
              )}
            </div>

            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <Clock className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Automatic Repayments</h3>
              {!isMobile && (
                <p className="text-gray-600">Automatic daily repayments make budgeting simple for your business.</p>
              )}
            </div>
          </div>

          <div className="text-center mt-6 md:mt-12">
            {visibleLogin &&
              <Button
                className="bg-intellend-green hover:bg-intellend-green/90"
                onClick={handleGetStarted}
              >
                Apply Now
              </Button>
            }
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-4 md:py-8 border-t">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 text-xs md:text-sm hidden">
            2026 Intellend. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
